import { useEffect, useRef, useState } from "react";

// A file dragged anywhere over the page is taken like one dropped on the dropzone,
// since the browser would otherwise open the PDF in place of the page. Returns
// whether a file is being dragged, for the dropzone to light up.
const hasFiles = (e) =>
  Boolean(e.dataTransfer) && Array.from(e.dataTransfer.types || []).indexOf("Files") !== -1;

const useFileDrop = (onFile, disabled) => {
  const [dragging, setDragging] = useState(false);
  // The listeners are added once, so they read the latest of these through refs
  const onFileRef = useRef(onFile);
  const disabledRef = useRef(disabled);
  onFileRef.current = onFile;
  disabledRef.current = disabled;

  useEffect(() => {
    // dragenter and dragleave come for every element crossed, so they are counted
    let depth = 0;

    const onEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth += 1;
      setDragging(true);
    };
    const onOver = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = disabledRef.current ? "none" : "copy";
    };
    const onLeave = (e) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && !disabledRef.current) onFileRef.current(file);
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  return dragging && !disabled;
};

export default useFileDrop;
